/**
 * UI Translations (Menu Views)
 *
 * Why this exists:
 * - The menu app already supports translating *menu content* (item name/description/category).
 * - This file adds lightweight translations for *UI labels* (buttons, placeholders, headings)
 *   so that switching language in the menu view updates the entire UI, not just menu items.
 *
 * Notes:
 * - The app currently uses 'en' and 'jp' as language codes.
 * - We intentionally keep this simple (no external i18n library) to reduce complexity.
 */
export type SupportedUILanguage = 'en' | 'jp';

type TranslationVars = Record<string, string | number | undefined>;
type UIStringValue = string | ((vars: TranslationVars) => string);

// Normalize language codes coming from URL/localStorage.
// Example: allow 'ja' or 'ja-JP' to map to our internal 'jp' code.
export function normalizeLanguage(language: string | undefined | null): SupportedUILanguage {
  const lang = (language || 'en').toLowerCase();
  if (lang === 'jp' || lang === 'ja' || lang.startsWith('ja-')) return 'jp';
  return 'en';
}

const UI_STRINGS: Record<SupportedUILanguage, Record<string, UIStringValue>> = {
  en: {
    // Generic
    loading: 'Loading...',
    menuNotFound: 'Menu not found',
    restaurantFallbackName: 'Restaurant',

    // Menu page
    noMenuItems: 'No menu items available',
    searchPlaceholder: 'Search menu items...',
    searchAriaLabel: 'Search menu items by name',
    clearSearch: 'Clear search',

    // QR section
    scanToViewMenu: 'Scan to View Menu',
    menuQrCodeAlt: 'Menu QR Code',
    menuHeaderAlt: 'Menu header',
    qrShareHelp:
      'Share this QR code to quickly access this menu on other mobile devices',

    // Categories / navigation
    openCategoriesList: 'Open categories list',
    scrollableCategories: 'Scrollable categories',
    restaurantInfoAndCategories: 'Restaurant information and categories',
    menuSectionTitle: 'Menu',
    goToCategoryAria: ({ category }) => `Go to ${String(category)}`,
    showCategoryAria: ({ category }) => `Show ${String(category)} category`,
    itemsCount: ({ count }) => `${Number(count) || 0} item${Number(count) === 1 ? '' : 's'}`,
    itemsInCategoryAria: ({ category, count }) =>
      `${Number(count) || 0} items in ${String(category)}`,

    // Language selection modal
    selectLanguageTitle: 'Select Language',
    currentLanguageBadge: 'Current',

    // Onboarding defaults
    onboardingTitle: 'Welcome to {restaurantName}!',
    onboardingSectionTitle: "I’m your digital restaurant assistant 🤖",
    onboardingSectionContent:
      'I have detailed knowledge of japanese food and this menu— ask me anything while you browse!',
    onboardingButtonText: 'I want to browse the menu first 🍣',
    onboardingFallbackRestaurant: 'this restaurant',

    // Default question chips
    defaultQuestion1: "It's my first time here, what should I order?",
    defaultQuestion2: 'Give me some recommendations',
    defaultQuestion3: 'What should I get to start?',
    navDefaultQuestion1: 'What should I order to start?',
    navDefaultQuestion2: 'Give me some japanese phrases I can use.',

    // Cart
    cartTitle: 'Your Order',
    cartInstructions: 'Show this screen to the store staff to order',
    cartEmpty: 'Your order is empty',
    clearOrder: 'Clear Order',
    close: 'Close',
    viewCart: 'View Cart →',
    yourOrderWithCount: ({ count }) =>
      `Your Order [${Number(count) || 0} item${Number(count) === 1 ? '' : 's'}]`,

    // Chat bar
    chatInitialMessage: 'What would you like to know?',
    botReplied: 'Bot replied:',
    tapToExpand: 'Tap to expand',
    chatWithHaru: 'Menu Assistant 🤖',
    thinking: 'thinking...',
    explainToMePlaceholder: 'Explain to me...',
    send: 'Send',
    itemNotFound: 'Item not found',
    chatChipRecommend: "What's a good starter?",
    chatChipDietaryRestriction: 'I have a dietary restriction...',

    // Menu item modal / question
    addToOrder: 'Add to Order',
    askAboutThisItem: 'Ask about this item',
    specialIngredientsTitle: 'Highlighted Ingredients',
    rarityUncommon: 'Uncommon',
    rarityRare: 'Special',
    rarityVeryRare: 'Special',
    raritySpecial: 'Special',
    ingredientOriginLabel: 'Origin',
    ingredientBrandLabel: 'Brand',
    askAboutMenuItemPlaceholder: 'Ask a question about this menu item',
    clearQuestion: 'Clear question',
    gettingAnswer: 'Getting answer...',
    fromFaq: 'From FAQ',
    createdByAi: 'Created by AI, please confirm with staff.',
    peopleAlsoAsk: 'People also ask',
    noAnswerAvailable: 'No answer available.',
    questionError: 'Sorry, I encountered an error. Please try again.',

    // Food / Drinks section toggle
    sectionFood: 'Food',
    sectionDrinks: 'Drinks',

    // Experimental "AI assistant picker" landing page
    landingIntroFallback:
      'Use your favourite AI assistant to explore this menu, or browse it yourself.',
    openInYourAssistant: 'Open this menu in your assistant',
    openInYourAssistantHelp:
      'We’ll hand your assistant a link to the full menu so it can help you decide what to order.',
    copyMenu: 'Copy menu text',
    menuCopied: 'Menu copied — paste it into your assistant.',
    menuCopyFailed: 'Could not copy the menu. Please try again.',
    landingOtherWays: 'Other ways to explore',
    useBuiltInAssistant: 'Use our menu assistant',
    browseFullMenu: 'Browse the full menu',

    // Menu item card aria labels
    viewDetailsAriaWithPrice: ({ name, priceYen }) =>
      `View details for ${String(name)}. Price ${Number(priceYen) || 0} yen.`,
    viewDetailsAriaNoPrice: ({ name }) => `View details for ${String(name)}.`,
    priceAria: ({ priceYen }) => `Price: ${Number(priceYen) || 0} yen`,
  },
  jp: {
    // Generic
    loading: '読み込み中…',
    menuNotFound: 'メニューが見つかりません',
    restaurantFallbackName: 'レストラン',

    // Menu page
    noMenuItems: 'メニューがありません',
    searchPlaceholder: 'メニューを検索…',
    searchAriaLabel: 'メニュー名で検索',
    clearSearch: '検索をクリア',

    // QR section
    scanToViewMenu: 'QRコードでメニューを見る',
    menuQrCodeAlt: 'メニューのQRコード',
    menuHeaderAlt: 'メニューのヘッダー画像',
    qrShareHelp:
      'このQRコードを共有すると、他の端末でもこのメニューをすぐに開けます',

    // Categories / navigation
    openCategoriesList: 'カテゴリー一覧を開く',
    scrollableCategories: 'スクロールできるカテゴリー一覧',
    restaurantInfoAndCategories: 'お店情報とカテゴリー',
    menuSectionTitle: 'メニュー',
    goToCategoryAria: ({ category }) => `${String(category)}へ移動`,
    showCategoryAria: ({ category }) => `${String(category)}を表示`,
    itemsCount: ({ count }) => `${Number(count) || 0}点`,
    itemsInCategoryAria: ({ category, count }) =>
      `${String(category)}に${Number(count) || 0}点`,

    // Language selection modal
    selectLanguageTitle: '言語を選択',
    currentLanguageBadge: '選択中',

    // Onboarding defaults
    onboardingTitle: '{restaurantName}へようこそ！',
    onboardingSectionTitle: 'デジタル接客スタッフです 🤖',
    onboardingSectionContent:
      '日本食やこのメニューについて詳しいです。見ながら何でも聞いてください！',
    onboardingButtonText: 'まずはメニューを見る 🍣',
    onboardingFallbackRestaurant: 'このお店',

    // Default question chips
    defaultQuestion1: 'はじめて来ました。おすすめはありますか？',
    defaultQuestion2: 'おすすめを教えてください',
    defaultQuestion3: 'まず何を頼むのがいいですか？',
    navDefaultQuestion1: 'まず何を頼むのがいい？',
    navDefaultQuestion2: '使える日本語フレーズを教えて',

    // Cart
    cartTitle: 'ご注文',
    cartInstructions: 'この画面を店員さんに見せて注文してください',
    cartEmpty: 'カートは空です',
    clearOrder: '注文をクリア',
    close: '閉じる',
    viewCart: 'カートを見る →',
    yourOrderWithCount: ({ count }) => `ご注文（${Number(count) || 0}点）`,

    // Chat bar
    chatInitialMessage: '何について知りたいですか？',
    botReplied: 'ボットの返信：',
    tapToExpand: 'タップして開く',
    chatWithHaru: 'メニューさん 🤖',
    thinking: '考え中…',
    explainToMePlaceholder: 'メニューについて聞いてみて…',
    send: '送信',
    itemNotFound: '該当の項目が見つかりません',
    chatChipRecommend: 'おすすめの前菜は？',
    chatChipDietaryRestriction: '食事制限があります…',

    // Menu item modal / question
    addToOrder: '注文に追加',
    askAboutThisItem: 'この料理について聞く',
    specialIngredientsTitle: '特別な食材',
    rarityUncommon: '少し珍しい',
    rarityRare: '珍しい',
    rarityVeryRare: '珍しい',
    raritySpecial: '特別',
    ingredientOriginLabel: '原産地',
    ingredientBrandLabel: 'ブランド',
    askAboutMenuItemPlaceholder: 'この料理について質問する',
    clearQuestion: '質問をクリア',
    gettingAnswer: '回答を取得中…',
    fromFaq: 'FAQより',
    createdByAi: 'AIが生成、スタッフに確認してください。',
    peopleAlsoAsk: 'よくある質問',
    noAnswerAvailable: '回答がありません。',
    questionError: 'エラーが発生しました。もう一度お試しください。',

    // Food / Drinks section toggle
    sectionFood: 'フード',
    sectionDrinks: 'ドリンク',

    // Experimental "AI assistant picker" landing page
    landingIntroFallback:
      'お好きなAIアシスタントでこのメニューを見るか、自分でメニューを見ることができます。',
    openInYourAssistant: 'お使いのアシスタントでこのメニューを開く',
    openInYourAssistantHelp:
      'アシスタントにメニュー全文へのリンクを渡すので、注文選びを手伝ってもらえます。',
    copyMenu: 'メニューをコピー',
    menuCopied: 'メニューをコピーしました。アシスタントに貼り付けてください。',
    menuCopyFailed: 'メニューをコピーできませんでした。もう一度お試しください。',
    landingOtherWays: 'その他の見方',
    useBuiltInAssistant: 'メニューアシスタントを使う',
    browseFullMenu: 'メニューをすべて見る',

    // Menu item card aria labels
    viewDetailsAriaWithPrice: ({ name, priceYen }) =>
      `${String(name)}の詳細を見る。価格は${Number(priceYen) || 0}円。`,
    viewDetailsAriaNoPrice: ({ name }) => `${String(name)}の詳細を見る。`,
    priceAria: ({ priceYen }) => `価格：${Number(priceYen) || 0}円`,
  }
};

export type UIStringKey = keyof typeof UI_STRINGS.en;

/**
 * Returns a translated UI string for the given key + language.
 * Falls back to English if the key doesn't exist for the selected language.
 */
export function t(
  key: UIStringKey,
  language: string | undefined,
  vars: TranslationVars = {}
): string {
  const lang = normalizeLanguage(language);
  const value = UI_STRINGS[lang][key] ?? UI_STRINGS.en[key];
  if (typeof value === 'function') {
    return value(vars);
  }

  // Simple variable replacement for templates like "{restaurantName}".
  return String(value).replace(/\{(\w+)\}/g, (_, varName) => String(vars[varName] ?? `{${varName}}`));
}

